import cx from 'classnames';
import { forwardRef, useCallback, useRef, useState } from 'react';

import {
  SLIDER_THUMB_CENTER_OFFSET_PX,
  SLIDER_TRACK_INSET_PX,
  sliderThumbCenterLeft,
  sliderThumbCenterRight,
  sliderThumbEdgeLeft,
} from './sliderTrackGeometry';

export interface RangeSliderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  min: number;
  max: number;
  step?: number;
  minValue: number;
  maxValue: number;
  disabled?: boolean;
  /** `pill` (default) is the two-knob range control; `trim` is the video-trimmer
   *  skin. Geometry is identical, so anything aligned to the track stays put. */
  variant?: 'pill' | 'trim';
  onChange: (minValue: number, maxValue: number) => void;
  /**
   * Colour of the active range between the thumbs. `Metal` (the default) is the
   * long-standing grey; `Ink` is `label-primary`, matching the prototype's
   * slider `fillColor`. The track behind it is unchanged either way.
   */
  fill?: 'Metal' | 'Ink';
}

export const RangeSlider = forwardRef<HTMLDivElement, RangeSliderProps>(
  (
    {
      className,
      min,
      max,
      step = 1,
      minValue,
      maxValue,
      disabled = false,
      variant = 'pill',
      onChange,
      fill = 'Metal',
      ...rest
    },
    ref,
  ) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState<'min' | 'max' | null>(null);

    const getValueFromPosition = useCallback(
      (clientX: number): number => {
        if (!trackRef.current) return min;

        const rect = trackRef.current.getBoundingClientRect();

        // Against the thumb's CENTRE travel, not the full track width. The
        // thumbs are laid out over `width - SLIDER_TRACK_INSET_PX`, so mapping
        // the pointer over the whole width put the two on different scales:
        // the cursor ran ahead of the handle, and the last half-thumb at each
        // end was unreachable.
        const travel = rect.width - SLIDER_TRACK_INSET_PX;

        const percentage =
          travel <= 0
            ? 0
            : Math.max(
                0,
                Math.min(
                  1,
                  (clientX - rect.left - SLIDER_THUMB_CENTER_OFFSET_PX) /
                    travel,
                ),
              );

        const rawValue = min + percentage * (max - min);

        // Snap to step
        const steppedValue = Math.round(rawValue / step) * step;

        return Math.max(min, Math.min(max, steppedValue));
      },
      [min, max, step],
    );

    const handlePointerDown = useCallback(
      (event: React.PointerEvent, thumb: 'min' | 'max') => {
        if (disabled) return;

        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        setIsDragging(thumb);
      },
      [disabled],
    );

    const handlePointerMove = useCallback(
      (event: React.PointerEvent) => {
        if (!isDragging || disabled) return;

        const newValue = getValueFromPosition(event.clientX);

        if (isDragging === 'min') {
          const clampedMin = Math.min(newValue, maxValue - step);
          onChange(clampedMin, maxValue);
        } else {
          const clampedMax = Math.max(newValue, minValue + step);
          onChange(minValue, clampedMax);
        }
      },
      [
        isDragging,
        disabled,
        getValueFromPosition,
        minValue,
        maxValue,
        step,
        onChange,
      ],
    );

    const handlePointerUp = useCallback(
      (event: React.PointerEvent) => {
        if (isDragging) {
          event.currentTarget.releasePointerCapture(event.pointerId);
          setIsDragging(null);
        }
      },
      [isDragging],
    );

    const handleTrackClick = useCallback(
      (event: React.MouseEvent) => {
        if (disabled || isDragging) return;

        const newValue = getValueFromPosition(event.clientX);
        const distanceToMin = Math.abs(newValue - minValue);
        const distanceToMax = Math.abs(newValue - maxValue);

        // Move the closer thumb
        if (distanceToMin <= distanceToMax) {
          const clampedMin = Math.min(newValue, maxValue - step);
          onChange(clampedMin, maxValue);
        } else {
          const clampedMax = Math.max(newValue, minValue + step);
          onChange(minValue, clampedMax);
        }
      },
      [
        disabled,
        isDragging,
        getValueFromPosition,
        minValue,
        maxValue,
        step,
        onChange,
      ],
    );

    // Percentages, less the thumb width — thumbs stay inside the 2.5px padding.
    const minPosition = ((minValue - min) / (max - min)) * 100;
    const maxPosition = ((maxValue - min) / (max - min)) * 100;

    // One skin for both handles. `trim` keeps the 27px hit target and is only
    // narrower in appearance, so touch accuracy doesn't regress. Its WIDTH is
    // fixed at 27px in both variants because `sliderTrackGeometry` derives the
    // travel from it — overlays like the ShowPath playhead line up on that.
    const thumbClassName = cx(
      'tw-absolute',
      'tw-top-[2.5px]',
      variant === 'trim' ? 'tw-h-[35px]' : 'tw-h-[27px]',
      'tw-w-[27px]',
      'tw-cursor-pointer',
      'tw-z-10',
      'tw-touch-none',
      'tw-select-none',
      'tw-flex',
      'tw-items-center',
      'tw-justify-center',
      disabled && 'tw-cursor-not-allowed',
      variant === 'pill' &&
        'tw-bg-white tw-rounded-full tw-shadow-i1 tw-border-2.5 tw-border-metal-50 dark:tw-border-metal-70',
    );

    // The trim handle: dark red, white grip line, and rounded on its OUTER face
    // only so it nests into the track's corner while its inner face sits flush
    // against the trimmed range.
    const gripHandle = (side: 'min' | 'max') =>
      variant === 'trim' ? (
        <span
          className={cx(
            'tw-flex tw-h-full tw-w-[18px] tw-items-center tw-justify-center',
            'tw-bg-red-60 dark:tw-bg-red-90 tw-shadow-i1',
            side === 'min'
              ? 'tw-mr-auto tw-rounded-l-10 tw-rounded-r-0'
              : 'tw-ml-auto tw-rounded-r-10 tw-rounded-l-0',
          )}
        >
          <span className="tw-h-[15px] tw-w-[2px] tw-rounded-full tw-bg-white" />
        </span>
      ) : null;

    return (
      <div
        ref={ref}
        className={cx(
          className,
          'tw-relative',
          variant === 'trim' ? 'tw-h-[40px]' : 'tw-h-[32px]',
          'tw-cursor-pointer',
          disabled && 'tw-cursor-not-allowed tw-opacity-40',
        )}
        {...rest}
      >
        {/* Track */}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          data-testid="range-slider-track-region"
          ref={trackRef}
          className={cx(
            'tw-absolute',
            'tw-inset-0',
            'tw-bg-fill-secondary',
            'tw-overflow-hidden',
            'tw-p-[2.5px]',
            variant === 'trim'
              ? 'tw-rounded-10 tw-border tw-border-red-60 dark:tw-border-red-90'
              : 'tw-rounded-full',
          )}
          onClick={handleTrackClick}
        >
          {/* Active range — pill only. The trim skin draws its own below, in the
              root's coordinate space, so its edges can land exactly on the
              handle centres. */}
          {variant === 'pill' && (
            <div
              className={cx(
                'tw-absolute',
                'tw-top-[0px]',
                'tw-bottom-[0px]',
                fill === 'Ink'
                  ? 'tw-bg-label-primary'
                  : 'tw-bg-metal-50 dark:tw-bg-metal-70',
                'tw-rounded-full',
              )}
              style={{
                left: `calc((100% - ${SLIDER_TRACK_INSET_PX}px) * ${
                  minPosition / 100
                })`,
                right: `calc((100% - ${SLIDER_TRACK_INSET_PX}px) * ${
                  (100 - maxPosition) / 100
                })`,
              }}
            />
          )}
        </div>

        {/* Trim tint, run handle-centre to handle-centre so the handles cover
            its ends instead of red poking out past them. */}
        {variant === 'trim' && (
          <div
            aria-hidden
            className="tw-absolute tw-top-[2.5px] tw-h-[35px] tw-bg-red-30 dark:tw-bg-red-70 tw-pointer-events-none"
            style={{
              left: sliderThumbCenterLeft(minPosition / 100),
              right: sliderThumbCenterRight(maxPosition / 100),
            }}
          />
        )}

        {/* Min thumb */}
        <div
          data-testid="range-slider-min-thumb"
          className={cx(thumbClassName, isDragging === 'min' && 'tw-z-20')}
          style={{ left: sliderThumbEdgeLeft(minPosition / 100) }}
          onPointerDown={(e) => handlePointerDown(e, 'min')}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        >
          {gripHandle('min')}
        </div>

        {/* Max thumb */}
        <div
          data-testid="range-slider-max-thumb"
          className={cx(thumbClassName, isDragging === 'max' && 'tw-z-20')}
          style={{ left: sliderThumbEdgeLeft(maxPosition / 100) }}
          onPointerDown={(e) => handlePointerDown(e, 'max')}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        >
          {gripHandle('max')}
        </div>
      </div>
    );
  },
);
